/**
 * ParkingAnalysisResults
 *
 * Design: Parking lot dashboard with occupancy meter
 * - Circular occupancy gauge
 * - Vehicle type breakdown with icons
 * - Capacity utilization visualization
 */
import React from "react";
import { ParkingAnalysisResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  ParkingCircle,
  Car,
  Truck,
  Bike,
  TrendingUp,
  TrendingDown,
  Gauge,
  BarChart3,
} from "lucide-react";

interface ParkingAnalysisResultsProps {
  results: ResultsType;
}

export const ParkingAnalysisResults: React.FC<ParkingAnalysisResultsProps> = ({
  results,
}) => {
  const isVideo = results.type === "video";
  const occupancyRate = results.average_occupancy_rate ?? 0;
  const occupancyPercentage = (occupancyRate * 100).toFixed(0);

  const getOccupancyColor = () => {
    if (occupancyRate >= 0.9)
      return {
        ring: "stroke-red-500",
        text: "text-red-400",
        bg: "from-red-500/20",
      };
    if (occupancyRate >= 0.7)
      return {
        ring: "stroke-amber-500",
        text: "text-amber-400",
        bg: "from-amber-500/20",
      };
    return {
      ring: "stroke-emerald-500",
      text: "text-emerald-400",
      bg: "from-emerald-500/20",
    };
  };

  const colors = getOccupancyColor();
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - occupancyRate * circumference;

  const getVehicleIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "car":
        return <Car className="w-5 h-5" />;
      case "truck":
      case "bus":
        return <Truck className="w-5 h-5" />;
      case "motorcycle":
      case "bike":
        return <Bike className="w-5 h-5" />;
      default:
        return <Car className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Occupancy Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Occupancy Gauge */}
        <div
          className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors.bg} via-slate-800/50 to-slate-900 border border-slate-700/50 p-6 flex flex-col items-center justify-center`}
        >
          <div className="absolute top-0 right-0 w-48 h-48 bg-slate-500/5 rounded-full blur-3xl" />

          <div className="relative mb-4">
            {/* Circular Progress */}
            <svg className="w-32 h-32 -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-slate-700"
              />
              <circle
                cx="64"
                cy="64"
                r="45"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                className={colors.ring}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: strokeDashoffset,
                  transition: "stroke-dashoffset 0.5s ease-out",
                }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${colors.text}`}>
                {occupancyPercentage}%
              </span>
            </div>
          </div>

          <p className="text-white font-medium">Occupancy Rate</p>
          <p className="text-slate-400 text-sm">
            {isVideo ? "Average" : "Current"}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <StatCard
            icon={<ParkingCircle className="w-5 h-5" />}
            label="Estimated Capacity"
            value={formatNumber(results.estimated_parking_capacity || 0)}
            color="primary"
          />
          <StatCard
            icon={<Car className="w-5 h-5" />}
            label={isVideo ? "Average Vehicles" : "Vehicles Detected"}
            value={formatNumber(
              isVideo
                ? results.average_vehicles || 0
                : results.total_vehicles_detected || 0
            )}
            color="cyan"
          />
          {isVideo && (
            <>
              <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Peak Vehicles"
                value={formatNumber(results.max_vehicles || 0)}
                color="emerald"
              />
              <StatCard
                icon={<TrendingDown className="w-5 h-5" />}
                label="Minimum"
                value={formatNumber(results.min_vehicles || 0)}
                color="amber"
              />
            </>
          )}
          {!isVideo && (
            <>
              <StatCard
                icon={<Gauge className="w-5 h-5" />}
                label="Available Spots"
                value={formatNumber(
                  (results.estimated_parking_capacity || 0) -
                    (results.total_vehicles_detected || 0)
                )}
                color="emerald"
              />
              <StatCard
                icon={<BarChart3 className="w-5 h-5" />}
                label="Total Detections"
                value={formatNumber(results.total_vehicles_detected || 0)}
                color="violet"
              />
            </>
          )}
        </div>
      </div>

      {/* Vehicle Type Distribution */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
            <Car className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Vehicle Types</h3>
            <p className="text-slate-400 text-sm">Distribution by category</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(results.vehicle_type_distribution).map(
            ([type, count]) => {
              const total = Object.values(
                results.vehicle_type_distribution
              ).reduce((a, b) => a + b, 0);
              const percentage =
                total > 0 ? ((count / total) * 100).toFixed(0) : 0;

              return (
                <div
                  key={type}
                  className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center mx-auto mb-3">
                    {getVehicleIcon(type)}
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {formatNumber(count)}
                  </p>
                  <p className="text-slate-400 text-sm capitalize">{type}</p>
                  <p className="text-primary-400 text-xs mt-1">{percentage}%</p>
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Detected Vehicles List (Image Only) */}
      {!isVideo &&
        results.detected_vehicles &&
        results.detected_vehicles.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Detected Vehicles</h3>
              <span className="text-slate-400 text-sm">
                {results.detected_vehicles.length} vehicles
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {results.detected_vehicles.slice(0, 12).map((vehicle, idx) => (
                <div
                  key={idx}
                  className="bg-slate-900/50 rounded-xl p-3 border border-slate-700/50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {getVehicleIcon(vehicle.vehicle_type)}
                    <span className="text-white font-medium capitalize text-sm">
                      {vehicle.vehicle_type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-xs">Confidence</span>
                    <span className="text-emerald-400 text-sm font-medium">
                      {(vehicle.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: "primary" | "cyan" | "emerald" | "amber" | "violet";
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const colorClasses = {
    primary: "bg-primary-500/20 text-primary-400",
    cyan: "bg-cyan-500/20 text-cyan-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    amber: "bg-amber-500/20 text-amber-400",
    violet: "bg-violet-500/20 text-violet-400",
  };

  return (
    <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
      <div className="flex items-center gap-2 mb-2">
        <div
          className={`w-8 h-8 rounded-lg ${colorClasses[color]} flex items-center justify-center`}
        >
          {icon}
        </div>
        <span className="text-slate-400 text-xs">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};
